<template>
  <div class="trials-inbox">
    <div class="card-title with-action">
      <div class="card-title-left">
        <SectionIcon name="teams" size="40" />
        <h3>Trials registrations</h3>
      </div>
      <div class="header-actions">
        <label class="open-toggle">
          <input type="checkbox" :checked="open" @change="toggleOpen($event.target.checked)" />
          <span>{{ open ? "Trials open" : "Trials closed" }}</span>
        </label>
        <button class="action-chip" @click="showEditor = !showEditor">
          <i class="bi bi-pencil-square"></i> {{ showEditor ? "Close setup" : "Trial setup" }}
        </button>
        <button class="action-chip" :disabled="!rows.length" @click="exportCsv">
          <i class="bi bi-download"></i> Export CSV
        </button>
      </div>
    </div>

    <TrialsEditor
      v-if="showEditor && config"
      :club-id="clubId"
      :initial-config="config"
      @close="showEditor = false"
      @saved="onConfigSaved"
    />

    <p class="muted">
      Public sign-up link:
      <code>{{ publicLink }}</code>
      <button class="link-button" @click="copyLink">copy</button>
      <span v-if="!open" class="closed-hint">— currently closed, the link shows "trials aren't open".</span>
    </p>

    <div class="filters">
      <button
        v-for="chip in statusChips"
        :key="chip.key"
        class="filter-chip"
        :class="{ active: statusFilter === chip.key }"
        @click="statusFilter = statusFilter === chip.key ? null : chip.key"
      >
        {{ chip.label }} ({{ countBy(chip.key) }})
      </button>
      <button
        class="filter-chip filter-chip-dupes"
        :class="{ active: statusFilter === 'duplicates' }"
        :disabled="!duplicateCount"
        @click="statusFilter = statusFilter === 'duplicates' ? null : 'duplicates'"
      >
        <i class="bi bi-people"></i> Duplicates ({{ duplicateCount }})
      </button>
    </div>
    <p v-if="statusFilter === 'duplicates'" class="muted dupes-hint">
      Same player name submitted more than once — usually a parent re-registering, or both parents
      registering. Grouped together, oldest first. <strong>Keep</strong> marks the one to hold on to (it
      has joined, paid, been offered, or is simply the first); check the DOB and email, then delete the rest
      with the <i class="bi bi-trash"></i> button. Rows that joined the app or paid a deposit can't be deleted.
    </p>

    <div v-if="selectable.length" class="bulk-bar">
      <label class="bulk-select-all">
        <input type="checkbox" :checked="allSelected" @change="toggleSelectAll($event.target.checked)" />
        <span>{{ selected.length ? `${selected.length} selected` : `Select all ${selectable.length} shown` }}</span>
      </label>
      <template v-if="selected.length">
        <input
          v-model="bulkTeam"
          class="bulk-team"
          placeholder="Team offered, e.g. U18 Reserves (used where a row has none)"
        />
        <button class="action-chip action-chip-primary" :disabled="bulkSending" @click="sendSelected">
          <i class="bi bi-envelope"></i>
          {{ bulkSending ? `Sending ${bulkProgress}…` : `Email sign-up link to ${selected.length} selected` }}
        </button>
      </template>
    </div>

    <table class="payments-table" v-if="filtered.length">
      <thead>
        <tr>
          <th class="select-col"></th>
          <th>Player</th>
          <th>Age</th>
          <th>Parent / contact</th>
          <th>Deposit</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="r in filtered" :key="r.id">
          <tr
            v-if="statusFilter === 'duplicates' && r._groupFirst"
            class="group-head"
          >
            <td colspan="7">{{ r.player_name }} — {{ r._groupSize }} registrations</td>
          </tr>
          <tr
            class="row"
            :class="{ selected: isSelected(r), 'dupe-keep': statusFilter === 'duplicates' && r._keep }"
            @click="expanded = expanded === r.id ? null : r.id"
          >
            <td class="select-col" @click.stop>
              <input
                v-if="canOffer(r) && r.email"
                type="checkbox"
                :checked="isSelected(r)"
                @change="toggleSelect(r, $event.target.checked)"
              />
            </td>
            <td>
              {{ r.player_name }}
              <span v-if="statusFilter === 'duplicates' && r._keep" class="pill pill-good">Keep</span>
              <span class="sub">{{ r.email }}</span>
              <span v-if="statusFilter === 'duplicates'" class="sub">
                DOB {{ r.dob || "—" }} · registered {{ formatDate(r.created_at) }}
              </span>
            </td>
            <td>{{ r.age_group || dobToAge(r.dob) || "—" }}</td>
            <td>
              {{ r.parent_name || "—" }}
              <span class="sub">{{ r.parent_contact }}</span>
            </td>
            <td>
              <span v-if="r.deposit_status === 'paid'" class="pill pill-good">Paid</span>
              <span v-else-if="r.deposit_status === 'pending'" class="pill pill-warn">
                Awaiting<template v-if="r.deposit_amount_pence"> {{ money(r.deposit_amount_pence) }}</template>
              </span>
              <span v-else class="muted">—</span>
            </td>
            <td>
              <select :value="r.status" @click.stop @change="setStatus(r, $event.target.value)">
                <option v-for="s in statuses" :key="s" :value="s">{{ statusLabel(s) }}</option>
              </select>
              <span v-if="r.promoted_user_id" class="pill pill-good">Joined</span>
              <span v-else-if="r.status === 'declined' && r.declined_notified_at" class="pill pill-warn">Notified</span>
              <span v-else-if="r.status !== 'declined' && r.invited_at" class="pill pill-warn">Invited</span>
            </td>
            <td class="row-end">
              <button
                v-if="statusFilter === 'duplicates' && canDelete(r)"
                class="icon-button"
                title="Delete this registration"
                :disabled="deleting === r.id"
                @click.stop="removeRow(r)"
              >
                <i class="bi bi-trash"></i>
              </button>
              <i class="bi" :class="expanded === r.id ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
            </td>
          </tr>
          <tr v-if="expanded === r.id" class="detail">
            <td colspan="7">
              <div class="detail-grid">
                <div><span class="k">DOB</span>{{ r.dob || "—" }}</div>
                <div><span class="k">Position</span>{{ r.preferred_position || "—" }}</div>
                <div><span class="k">Previous club</span>{{ r.previous_club || "—" }}</div>
                <div><span class="k">Trials attending</span>{{ parseList(r.sessions_attending) || r.session_label || "—" }}</div>
                <!-- Free text from a textarea on the sign-up form, so it can
                     run to several lines — kept full width and newline-aware. -->
                <div class="wide experience"><span class="k">Experience</span>{{ r.experience || "—" }}</div>
                <div class="wide"><span class="k">Registered</span>{{ formatDate(r.created_at) }}</div>
              </div>
              <label class="notes team-offered">
                Team offered <span class="hint">— named in the offer email as "a place in the …"</span>
                <input v-model="r._team" :placeholder="r.age_group || 'e.g. U18 Reserves'" @blur="saveTeam(r)" />
              </label>
              <label class="notes">
                Notes
                <textarea v-model="r._notes" rows="2" @blur="saveNotes(r)"></textarea>
              </label>
              <div class="detail-actions">
                <button
                  v-if="r.deposit_status !== 'paid' && r.deposit_amount_pence"
                  class="action-chip"
                  @click.stop="markDepositPaid(r)"
                >
                  <i class="bi bi-bank"></i> Mark deposit paid (bank transfer)
                </button>
                <button
                  v-if="r.status === 'declined' && !r.promoted_user_id"
                  class="action-chip"
                  :disabled="sending === r.id || !r.email"
                  @click.stop="sendDeclineEmail(r, { confirm: true })"
                >
                  <i class="bi bi-envelope"></i>
                  {{
                    sending === r.id
                      ? "Sending…"
                      : r.declined_notified_at
                        ? "Re-send decline email"
                        : "Email them they were unsuccessful"
                  }}
                </button>
                <button
                  v-else-if="!r.promoted_user_id"
                  class="action-chip action-chip-primary"
                  :disabled="sending === r.id || !r.email"
                  @click.stop="sendSignupLink(r)"
                >
                  <i class="bi bi-envelope"></i>
                  {{
                    sending === r.id
                      ? "Sending…"
                      : r.invited_at
                        ? "Re-send sign-up link"
                        : "Email sign-up link (offer them a place)"
                  }}
                </button>
                <span v-else class="pill pill-good">
                  <i class="bi bi-check-circle"></i> Joined the app
                </span>
                <button class="action-chip" @click.stop="copyRegisterLink">
                  <i class="bi bi-clipboard"></i> Copy registration link
                </button>
                <button
                  v-if="canDelete(r)"
                  class="action-chip action-chip-danger"
                  :disabled="deleting === r.id"
                  @click.stop="removeRow(r)"
                >
                  <i class="bi bi-trash"></i> {{ deleting === r.id ? "Deleting…" : "Delete registration" }}
                </button>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <p v-else class="muted">
      No registrations{{ statusFilter === "duplicates" ? " look like duplicates" : statusFilter ? " with this status" : " yet" }}.
    </p>
    <p class="ok-text" v-if="sentTo">{{ sentTo.includes("@") ? `${sentKind} sent to ${sentTo}.` : `${sentTo}.` }}</p>
    <p class="error-text" v-if="error">{{ error }}</p>
  </div>
</template>

<script>
import { Trials } from "@/services/trials-service.js";
import { getPaymentsCurrency } from "@/config/tenant";
import SectionIcon from "@/components/SectionIcon/index.vue";
import TrialsEditor from "./TrialsEditor.vue";

const STATUSES = ["new", "contacted", "trialing", "offered", "signed", "declined"];

export default {
  name: "TrialsInbox",
  components: { SectionIcon, TrialsEditor },
  props: { clubId: { type: String, required: true } },
  data() {
    return {
      rows: [],
      config: null,
      showEditor: false,
      open: false,
      statuses: STATUSES,
      statusFilter: null,
      sending: null,
      deleting: null,
      sentTo: null,
      sentKind: "Sign-up link",
      selected: [],
      bulkTeam: "",
      bulkSending: false,
      bulkProgress: "",
      expanded: null,
      error: "",
      statusChips: [
        { key: "new", label: "New" },
        { key: "contacted", label: "Contacted" },
        { key: "trialing", label: "Trialing" },
        { key: "offered", label: "Offered" },
        { key: "signed", label: "Signed" },
        { key: "declined", label: "Declined" },
      ],
    };
  },
  computed: {
    publicLink() {
      return `${window.location.origin}/trials/${encodeURIComponent(this.clubId)}`;
    },
    filtered() {
      if (!this.statusFilter) return this.rows;
      if (this.statusFilter === "duplicates") return this.duplicateRows;
      return this.rows.filter((r) => r.status === this.statusFilter);
    },
    /**
     * Registrations that share a player name, grouped and ordered oldest
     * first, each tagged with whether it's the one to keep. Name only (not
     * name + DOB): the typical duplicate is the other parent typing the
     * DOB slightly differently, and the row shows DOB + email so the admin
     * can tell a genuine namesake apart.
     */
    duplicateRows() {
      const groups = new Map();
      for (const r of this.rows) {
        const key = this.nameKey(r.player_name);
        if (!key) continue;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(r);
      }
      const out = [];
      for (const rows of groups.values()) {
        if (rows.length < 2) continue;
        rows.sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
        // Keep the row with the most history behind it; ties go to the oldest.
        const keep = rows.reduce((best, r) => (this.progressRank(r) > this.progressRank(best) ? r : best), rows[0]);
        rows.forEach((r, i) => {
          out.push({ ...r, _groupFirst: i === 0, _groupSize: rows.length, _keep: r === keep });
        });
      }
      return out;
    },
    duplicateCount() {
      return this.duplicateRows.length;
    },
    /** Rows in the current filter that can still be emailed a link. */
    selectable() {
      return this.filtered.filter((r) => this.canOffer(r) && r.email);
    },
    allSelected() {
      return this.selectable.length > 0 && this.selectable.every((r) => this.selected.includes(r.id));
    },
  },
  watch: {
    // The selection belongs to the filter it was made in: switching from
    // "New" to "Offered" must not carry hidden rows into a bulk send.
    statusFilter() {
      this.selected = [];
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const res = await Trials.list(this.clubId);
        this.open = res.data.open;
        this.config = res.data.config;
        this.rows = (res.data.registrations || []).map((r) => ({
          ...r,
          _notes: r.notes || "",
          _team: r.offered_team || "",
        }));
      } catch (e) {
        this.error = "Could not load trials registrations.";
      }
    },
    money(pence) {
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: getPaymentsCurrency(),
      }).format((pence || 0) / 100);
    },
    onConfigSaved(cfg) {
      this.config = cfg;
      this.showEditor = false;
    },
    countBy(status) {
      return this.rows.filter((r) => r.status === status).length;
    },
    statusLabel(s) {
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    async toggleOpen(open) {
      await Trials.setOpen(this.clubId, open);
      this.open = open;
    },
    async setStatus(row, status) {
      const previous = row.status;
      row.status = status;
      await Trials.update(this.clubId, row.id, { status });

      // Offering the place is exactly the moment the family needs the sign-up
      // link, so prompt here rather than making the admin remember. Asked
      // rather than sent silently: a bulk tidy-up of 310 rows must not email
      // 310 families by accident, and the same row can be re-offered.
      if (
        status === "offered" &&
        previous !== "offered" &&
        !row.promoted_user_id &&
        !row.invited_at &&
        row.email
      ) {
        if (
          window.confirm(
            `Email the sign-up link to ${row.email}?\n\n` +
              `They'll use it to create ${row.player_name}'s account and pick a squad.`,
          )
        ) {
          await this.sendSignupLink(row);
        }
      }

      // Declining never emails by itself — ask, so the club can still tell
      // some families in person and a status tidy-up stays silent.
      if (status === "declined" && previous !== "declined") {
        this.selected = this.selected.filter((id) => id !== row.id);
        if (!row.promoted_user_id && !row.declined_notified_at && row.email) {
          await this.sendDeclineEmail(row, { confirm: true });
        }
      }
    },
    /**
     * Email one family their registration link. The email names the team
     * they're offered, so make sure we have one before sending.
     */
    async sendSignupLink(row, { team, quiet = false } = {}) {
      if (this.sending === row.id) return false;
      let offeredTeam = (team || row._team || "").trim();
      if (!offeredTeam && !quiet) {
        const typed = window.prompt(
          `Which team is ${row.player_name} being offered a place in?\n\n` +
            `This fills {team_name} in the offer email.`,
          row.age_group || "",
        );
        if (typed === null) return false;
        offeredTeam = typed.trim();
      }
      this.sending = row.id;
      this.error = null;
      try {
        const { data } = await Trials.sendSignupLink(this.clubId, row.id, offeredTeam);
        if (data && data.success) {
          row._team = offeredTeam;
          row.offered_team = offeredTeam;
          if (data.depositStatus) row.deposit_status = data.depositStatus;
          if (data.depositAmountPence != null) row.deposit_amount_pence = data.depositAmountPence;
          row.invited_at = new Date().toISOString().slice(0, 19).replace("T", " ");
          if (row.status === "new" || row.status === "contacted") {
            row.status = "offered";
          }
          this.sentKind = "Sign-up link";
          this.sentTo = data.sentTo;
          window.setTimeout(() => (this.sentTo = null), 4000);
          return true;
        }
        this.error = (data && data.error) || "Could not send the sign-up link.";
      } catch (e) {
        const d = e && e.response && e.response.data;
        this.error = (d && d.error) || "Could not send the sign-up link.";
      } finally {
        this.sending = null;
      }
      return false;
    },
    /** A place can still be offered: not already joined, not declined. */
    canOffer(row) {
      return !row.promoted_user_id && row.status !== "declined";
    },
    /** Joined-the-app and paid-deposit rows are the ones to keep, never delete. */
    canDelete(row) {
      return !row.promoted_user_id && row.deposit_status !== "paid";
    },
    nameKey(name) {
      return String(name || "")
        .toLowerCase()
        .replace(/[^a-zÀ-ɏ\s]/g, "")
        .replace(/\s+/g, " ")
        .trim();
    },
    progressRank(row) {
      if (row.promoted_user_id) return 5;
      if (row.deposit_status === "paid") return 4;
      if (row.invited_at || row.status === "offered" || row.status === "signed") return 3;
      if (row.status === "trialing" || row.status === "contacted") return 2;
      if ((row.notes || "").trim() || (row.offered_team || "").trim()) return 1;
      return 0;
    },
    async removeRow(row) {
      if (
        !window.confirm(
          `Delete ${row.player_name}'s registration from ${this.formatDate(row.created_at)} (${row.email || "no email"})?\n\n` +
            `This can't be undone. The family isn't emailed.`,
        )
      ) {
        return;
      }
      this.deleting = row.id;
      this.error = null;
      try {
        const { data } = await Trials.remove(this.clubId, row.id);
        if (data && data.success) {
          this.rows = this.rows.filter((r) => r.id !== row.id);
          this.selected = this.selected.filter((id) => id !== row.id);
          if (this.expanded === row.id) this.expanded = null;
        } else {
          this.error = (data && data.error) || "Could not delete the registration.";
        }
      } catch (e) {
        const d = e && e.response && e.response.data;
        this.error = (d && d.error) || "Could not delete the registration.";
      } finally {
        this.deleting = null;
      }
    },
    /** Tell a declined family they were unsuccessful (wording in Trial setup). */
    async sendDeclineEmail(row, { confirm = false } = {}) {
      if (this.sending === row.id) return false;
      if (
        confirm &&
        !window.confirm(
          `Email ${row.email} to let them know ${row.player_name} hasn't been offered a place?\n\n` +
            `Cancel keeps them Declined without emailing. The wording is under Trial setup → Decline email.`,
        )
      ) {
        return false;
      }
      this.sending = row.id;
      this.error = null;
      try {
        const { data } = await Trials.sendDeclineEmail(this.clubId, row.id);
        if (data && data.success) {
          row.declined_notified_at = new Date().toISOString().slice(0, 19).replace("T", " ");
          this.sentKind = "Decline email";
          this.sentTo = data.sentTo;
          window.setTimeout(() => (this.sentTo = null), 4000);
          return true;
        }
        this.error = (data && data.error) || "Could not send the decline email.";
      } catch (e) {
        const d = e && e.response && e.response.data;
        this.error = (d && d.error) || "Could not send the decline email.";
      } finally {
        this.sending = null;
      }
      return false;
    },
    isSelected(row) {
      return this.selected.includes(row.id);
    },
    toggleSelect(row, on) {
      this.selected = on
        ? [...new Set([...this.selected, row.id])]
        : this.selected.filter((id) => id !== row.id);
    },
    toggleSelectAll(on) {
      this.selected = on ? this.selectable.map((r) => r.id) : [];
    },
    /**
     * Email every selected family, one after another. Each row needs a team
     * name: its own if set, otherwise the one typed in the bulk bar. Rows
     * with neither are skipped and reported rather than guessed at.
     */
    async sendSelected() {
      const rows = this.selectable.filter((r) => this.selected.includes(r.id));
      if (!rows.length) return;
      const fallback = this.bulkTeam.trim();
      const missing = rows.filter((r) => !(r._team || "").trim() && !fallback);
      if (missing.length) {
        this.error = `Type a team in the box first — ${missing.length} of the selected have no "Team offered".`;
        return;
      }
      if (
        !window.confirm(
          `Email the offer and sign-up link to ${rows.length} famil${rows.length === 1 ? "y" : "ies"}?` +
            (fallback ? `\n\nRows without a team set will be offered "${fallback}".` : ""),
        )
      ) {
        return;
      }
      this.bulkSending = true;
      this.error = null;
      let sent = 0;
      const failed = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        this.bulkProgress = `${i + 1} of ${rows.length}`;
        const ok = await this.sendSignupLink(row, {
          team: (row._team || "").trim() || fallback,
          quiet: true,
        });
        if (ok) {
          sent++;
          this.selected = this.selected.filter((id) => id !== row.id);
        } else {
          failed.push(row.player_name);
        }
      }
      this.bulkSending = false;
      this.bulkProgress = "";
      this.sentTo = `${sent} email${sent === 1 ? "" : "s"} sent`;
      window.setTimeout(() => (this.sentTo = null), 6000);
      this.error = failed.length ? `Could not send to: ${failed.join(", ")}` : null;
    },
    /** Deposits taken outside the app (bank transfer, cash at the clubrooms). */
    async markDepositPaid(row) {
      if (!window.confirm(`Mark ${row.player_name}'s ${this.money(row.deposit_amount_pence)} deposit as paid?`)) return;
      await Trials.update(this.clubId, row.id, { deposit_status: "paid" });
      row.deposit_status = "paid";
    },
    async saveTeam(row) {
      const team = (row._team || "").trim();
      if (team === (row.offered_team || "")) return;
      await Trials.update(this.clubId, row.id, { offered_team: team });
      row.offered_team = team;
    },
    async saveNotes(row) {
      if (row._notes === (row.notes || "")) return;
      await Trials.update(this.clubId, row.id, { notes: row._notes });
      row.notes = row._notes;
    },
    parseList(json) {
      try {
        const arr = JSON.parse(json);
        return Array.isArray(arr) ? arr.join(", ") : "";
      } catch (e) {
        return "";
      }
    },
    dobToAge(dob) {
      if (!dob) return "";
      const y = new Date(dob).getFullYear();
      return y ? `b. ${y}` : "";
    },
    formatDate(v) {
      if (!v) return "";
      return new Date(v.replace(" ", "T")).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    copyLink() {
      navigator.clipboard.writeText(this.publicLink);
    },
    copyRegisterLink() {
      navigator.clipboard.writeText(
        `${window.location.origin}/register/${encodeURIComponent(this.clubId)}`,
      );
    },
    exportCsv() {
      const cols = [
        "player_name", "dob", "age_group", "team_preference", "preferred_position",
        "parent_name", "parent_contact", "email", "experience", "previous_club",
        "deposit_status", "deposit_amount_pence", "status", "offered_team", "created_at",
      ];
      const header = cols.map((c) => c.replace(/_/g, " "));
      const rows = this.rows.map((r) => cols.map((c) => r[c] ?? ""));
      const csv = [header, ...rows]
        .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      link.download = `trials-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};
</script>

<style scoped lang="scss">
.trials-inbox {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
}

.card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  .card-title-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  h3 {
    margin: 0;
    font-size: 1.05rem;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.open-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    accent-color: #198754;
  }
}

.muted {
  color: #6c757d;
  font-size: 0.85rem;

  code {
    background: #f1f3f5;
    padding: 1px 6px;
    border-radius: 4px;
  }
}

.closed-hint {
  color: #b8860b;
}

.filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 12px 0;
}

.filter-chip {
  border: 1px solid #ced4da;
  background: #fff;
  color: #343a40; // global `button { color: white }` would hide the label
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 0.8rem;
  cursor: pointer;

  &.active {
    background: #343a40;
    color: #fff;
    border-color: #343a40;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.filter-chip-dupes {
  border-color: #b8860b;
  color: #8a6508;

  &.active {
    background: #b8860b;
    border-color: #b8860b;
    color: #fff;
  }
}

.dupes-hint {
  margin: -4px 0 10px;
}

.payments-table .group-head td {
  background: #fff8e6;
  font-weight: 700;
  font-size: 0.82rem;
  color: #8a6508;
  border-top: 2px solid #f3e2b3;
}

.payments-table .row.dupe-keep td {
  background: #f3faf5;
}

.row-end {
  white-space: nowrap;
}

.icon-button {
  background: none;
  border: none;
  color: #dc3545;
  padding: 0 8px 0 0;
  cursor: pointer;
  font-size: 0.95rem;

  &:disabled {
    opacity: 0.5;
  }
}

.payments-table {
  // Force table layout — a global settings rule stacks td as display:block.
  display: table;
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  thead {
    display: table-header-group;
  }

  tbody {
    display: table-row-group;
  }

  tr {
    display: table-row;
  }

  th, td {
    display: table-cell;
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #f1f3f5;
    vertical-align: top;
  }

  .row {
    cursor: pointer;

    &.selected td {
      background: #eef7f1;
    }
  }

  .sub {
    display: block;
    color: #6c757d;
    font-size: 0.78rem;
  }

  select {
    padding: 3px 6px;
    border: 1px solid #ced4da;
    border-radius: 6px;
  }
}

.pill {
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.72rem;
  font-weight: 700;

  &-good { background: #d1e7dd; color: #0f5132; }
  &-warn { background: #fff3cd; color: #856404; }
}

.detail {
  background: #fafbfc;

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px 20px;
    margin-bottom: 10px;

    .wide { grid-column: 1 / -1; }

    // Preserve the line breaks the parent typed into the experience textarea.
    .experience {
      white-space: pre-line;
    }

    .k {
      display: inline-block;
      min-width: 100px;
      color: #6c757d;
      font-size: 0.78rem;
    }
  }

  .notes {
    display: block;
    font-size: 0.82rem;
    color: #495057;

    textarea {
      width: 100%;
      margin-top: 4px;
      border: 1px solid #ced4da;
      border-radius: 6px;
      padding: 6px 8px;
    }
  }

  .detail-actions {
    margin-top: 10px;
  }

  .team-offered {
    margin-bottom: 8px;

    .hint {
      font-weight: 400;
      color: #6c757d;
    }

    input {
      display: block;
      width: 100%;
      max-width: 360px;
      margin-top: 4px;
      border: 1px solid #ced4da;
      border-radius: 6px;
      padding: 6px 8px;
    }
  }
}

.select-col {
  width: 28px;
  text-align: center;
}

.bulk-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 0 0 10px;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.85rem;

  .bulk-select-all {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    cursor: pointer;
  }

  .bulk-team {
    flex: 1 1 260px;
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 6px 8px;
    font-size: 0.85rem;
  }
}

.action-chip {
  border: 1px solid #ced4da;
  background: #fff;
  color: #212529;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.82rem;
  cursor: pointer;

  &:disabled { opacity: 0.5; }
}

.link-button {
  border: none;
  background: none;
  color: #0d6efd;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.82rem;
}

.action-chip-primary {
  border-color: #198754;
  background: #198754;
  color: #fff;
  font-weight: 600;
}

.action-chip-danger {
  border-color: #dc3545;
  color: #dc3545;
  margin-left: auto;
}

.error-text {
  color: #dc3545;
  font-size: 0.85rem;
}

.ok-text {
  color: #198754;
  font-size: 0.85rem;
}
</style>
